import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribeStatus('Thank you for subscribing!');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 3000);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="home" className="relative min-h-screen bg-gradient-to-br from-[#1a1f3a] via-[#2d2654] to-[#4a3f7a] overflow-hidden">
        {/* Particle Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute top-40 left-60 w-1 h-1 bg-white rounded-full"></div>
          <div className="absolute top-60 right-80 w-2 h-2 bg-white rounded-full"></div>
          <div className="absolute bottom-40 right-40 w-1 h-1 bg-white rounded-full"></div>
          <div className="absolute top-80 left-1/3 w-1 h-1 bg-white rounded-full"></div>
          <div className="absolute bottom-60 left-1/4 w-2 h-2 bg-white rounded-full"></div>
        </div>

        {/* Floating Geometric Shapes */}
        <div className="absolute right-0 top-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute right-40 top-60 w-80 h-80 bg-blue-400/15 rounded-3xl rotate-12 blur-2xl"></div>
        <div className="absolute right-20 bottom-40 w-64 h-64 bg-purple-300/20 rounded-2xl -rotate-12 blur-2xl"></div>

        {/* Navigation */}
        <nav className="relative z-10 h-20 flex items-center px-28 bg-black/30">
          <div className="text-white text-2xl font-medium tracking-wide">FRALI SH.P.K.</div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex items-center h-[calc(100vh-80px)] px-28">
          <div className="max-w-2xl">
            <div className="inline-block px-6 py-2 border border-white/40 rounded-full text-white text-sm mb-20">
              iOS Development Experts
            </div>
            <h1 className="text-7xl font-extralight text-white leading-tight mb-16">
              Modern Applications<br />
              for iOS Platform
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed max-w-lg">
              Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="bg-[#f5f6f8] py-32 px-28">
        <div className="mb-20">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-[#6b7280] text-xs uppercase tracking-widest mb-6">OUR PRODUCTS</div>
              <h2 className="text-6xl font-extrabold text-[#1f2937] leading-tight">
                Enhancing Your<br />
                Mobile Experience
              </h2>
            </div>
            <p className="text-lg text-gray-600 max-w-sm mt-16">
              Designed for security and performance
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-12">
          {/* VPN Card */}
          <div className="bg-white rounded-3xl p-14 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 flex items-center justify-center mb-10">
              <i className="ri-shield-check-line text-6xl bg-gradient-to-br from-blue-500 to-purple-600 bg-clip-text text-transparent"></i>
            </div>
            <h3 className="text-4xl font-bold text-black mb-8">VPN</h3>
            <p className="text-lg text-[#4b5563] leading-relaxed max-w-sm">
              Secure your connection and protect your privacy with military-grade encryption. Browse anonymously across all networks.
            </p>
          </div>

          {/* Cleaner Card */}
          <div className="bg-white rounded-3xl p-14 shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 flex items-center justify-center mb-10">
              <i className="ri-brush-line text-6xl bg-gradient-to-br from-green-500 to-teal-600 bg-clip-text text-transparent"></i>
            </div>
            <h3 className="text-4xl font-bold text-black mb-8">Cleaner</h3>
            <p className="text-lg text-[#4b5563] leading-relaxed max-w-sm">
              Optimize device performance by removing junk files, clearing cache, and managing storage efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="bg-white py-40">
        <div className="flex">
          {/* Left Content */}
          <div className="w-[55%] pl-28 pr-16 flex flex-col justify-center">
            <h2 className="text-5xl font-semibold text-black leading-snug mb-16">
              Expert Team<br />
              Building Modern<br />
              Applications
            </h2>
          </div>

          {/* Right Decorative */}
          <div className="w-[45%] relative overflow-hidden bg-gradient-to-br from-[#fce7f3] to-[#fdf2f8] h-[600px]">
            <div className="absolute top-20 left-10 w-80 h-64 bg-[#f9a8d4] opacity-40 rounded-[60px] -rotate-[15deg]"></div>
            <div className="absolute top-40 right-10 w-72 h-56 bg-[#f472b6] opacity-60 rounded-[50px] rotate-[8deg]"></div>
            <div className="absolute bottom-20 left-20 w-64 h-72 bg-[#ec4899] opacity-50 rounded-[40px] -rotate-[22deg]"></div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="bg-[#1f2937] pt-24 pb-20 px-28">
        {/* Top Row */}
        <div className="flex justify-between items-center mb-20">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/45ba07cd-0da2-4221-b546-9371e90d579d.png" alt="FRALI SH.P.K. Logo" className="h-12 w-auto" />
            <div className="text-white text-2xl font-medium tracking-wide">FRALI SH.P.K.</div>
          </div>
          <div className="text-[#9ca3af] text-lg">Get in touch with our team</div>
        </div>

        {/* Bottom Section */}
        <div className="flex gap-10 mb-20">
          {/* Left Visual */}
          <div className="w-[30%]">
            <img 
              src="https://public.readdy.ai/ai/img_res/eb0ddb45fa98707412caad72f997546e.jpg" 
              alt="Office workspace" 
              className="w-full h-60 object-cover rounded-2xl border border-white/10"
            />
          </div>

          {/* Center Text */}
          <div className="w-[35%] flex items-center pl-10">
            <h3 className="text-white text-3xl font-light leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
              Creating secure<br />
              and efficient<br />
              mobile solutions
            </h3>
          </div>

          {/* Right Subscribe */}
          <div className="w-[35%]">
            <p className="text-[#9ca3af] text-sm leading-relaxed mb-8 max-w-xs">
              Stay updated with our latest releases and features
            </p>
            <form onSubmit={handleSubscribe} className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-[#374151] text-white text-base px-5 py-3 rounded-l-xl outline-none placeholder-gray-400"
                required
              />
              <button
                type="submit"
                className="bg-[#3b82f6] text-white px-8 py-3 rounded-r-xl font-medium hover:bg-[#2563eb] transition-colors duration-200 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Subscribe
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </form>
            {subscribeStatus && (
              <div className="mt-4 text-green-400 text-sm">{subscribeStatus}</div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="flex items-center gap-12 pt-14 border-t border-white/10">
          <div className="flex items-center gap-3">
            <i className="ri-map-pin-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">Address</div>
              <div className="text-white text-base">SHEQ I MADH</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-building-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">City</div>
              <div className="text-white text-base">FIER</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-mail-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">Zip</div>
              <div className="text-white text-base">9306</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-global-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">Country</div>
              <div className="text-white text-base">Albania</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-mail-send-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">Email</div>
              <div className="text-white text-base">help@fralishpk.com</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <i className="ri-phone-line text-xl text-[#9ca3af]"></i>
            <div>
              <div className="text-[#9ca3af] text-xs uppercase tracking-wider mb-1">Number</div>
              <div className="text-white text-base">+1 3603646935</div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <div className="text-[#9ca3af] text-sm">
            © 2025 FRALI SH.P.K. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
}
